<template>
  <div class="product">
    <a-row v-if="customSpu && !hideInfo"
           class="spuInfo">
      <div ref="hiddenContainer"
           class="hidden"></div>
      <a-col :span="6">
        <div v-if="hiddenContainer && is_custom"
             class="viewer">
          <ImageCustomizeViewer v-if="image"
                                :id="image.id + ''"
                                mode="view"
                                :container="hiddenContainer"
                                :template-basic-parameters="templateBasicParameters(toUrl(findCustomableImage(selectedColor as any, image))!, selectedColor?.value!, image.width, image.height)"
                                :custom-area="assignCustomizedData(image.custom_areas, image.width, image.customable_image_scale!, image.boundingRect!)"
                                :customized-image="toUrl(image.customized_image)" />
        </div>
        <img v-else
             :src="toUrl(spuPickImage)"
             class="viewer" />
      </a-col>
      <a-col :span="18">
        <div>{{ customSpu?.name }}</div>
        <div>{{ customSpu?.spu_name }}</div>
      </a-col>
    </a-row>
    <div class="selling-price">
      <PriceBreakdown v-if="customSpu?.price_detail"
                      :custom-price-detail="customSpu?.price_detail"
                      :is-order="false"
                      :is-custom="true"
                      :is-add-cart="true"
                      :quantity="quantity"
                      class="price-box" />
    </div>
    <div v-if="customSpu">
      <SelectItem :is_custom="is_custom"
                  :alt_image="alt_image"
                  :colors="colors"
                  :sizes="sizes"
                  :image="image"
                  :selected-color="selectedColor"
                  :selected-size="selectedSize"
                  :custom_price="customSpu?.price_detail.custom_price"
                  @quality-change="qualityChange"
                  @color-change="colorChangeClick"
                  @size-change="sizeChangeClick"></SelectItem>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toUrl } from '@/utils'
import { ImageCustomizeEditor as ImageCustomizeViewer } from '@iota-cn/ui-components'
// import ImageCustomizeEditor from '../ImageCustomizeEditor.vue'
import { message } from 'ant-design-vue'
import BigNumber from "bignumber.js"
import { computed, ref, watch } from 'vue'
import { useShoppingCart } from '../..'
import type { CartData, CartDataSku, Color, CustomizedImage, AltImage, CustomPrice } from '../../types'
import { convertSpuToSimpleSpu, convertToCustomizedImage, findCustomableImage, sortBy, templateBasicParameters, assignCustomizedData } from '../../utils'
import PriceBreakdown, { type Technology } from './PriceBreakdown.vue'
import { selectedSpuData } from "../../index"
import { useI18n } from 'vue-i18n'
import SelectItem from "./SelectItem.vue"
const currentCustomSpuData = selectedSpuData()
const { t } = useI18n()

defineProps(['hideInfo'])
const emits = defineEmits(['colorChange'])
BigNumber.config({ ROUNDING_MODE: 1 })
const shoppingCart = useShoppingCart()
const customPrice = ref<number>(0)
const customSpu = ref<CartData | undefined>()
const quantity = ref(1)
const selectedCustomSkus = ref<CartDataSku[]>([])
const filterByColorCustomSkus = ref<CartDataSku[]>([])

const filterSkusByColor = (color: Color, arr: CartDataSku[]) => {
  const r = arr.filter(k => {
    return k.color?.value === color.value
  })
  return r
}
const qualityChange = (event: any) => {
  quantity.value = event.quantity
}
// @Todo change to computed?
const colors = ref<Color[]>([])
const selectedColor = ref<Color>()
const selectedSize = ref<string>('')

watch(() => [selectedColor.value, selectedSize.value], () => {
  if (!customSpu.value)
    return
  let selectShape: any
  // 三个父页面的传递参数不一致 需要额外判断
  if (customSpu.value.customSkus) {
    selectShape = customSpu.value.customSkus?.find((r: any) => r.color.value == selectedColor.value?.value && r.size == selectedSize.value)
    if (selectShape && selectShape.sku.price)
      customSpu.value.price_detail.sku_price = new BigNumber(selectShape.sku.price).toFixed(2) as any
  }
  else if (customSpu.value.skus) {
    selectShape = customSpu.value.skus?.find((r: any) => r.color.value == selectedColor.value?.value && r.size == selectedSize.value)
    if (selectShape) {
      if (selectShape.price)
        customSpu.value.price_detail.sku_price = new BigNumber(selectShape.price).toFixed(2) as any
      if (selectShape.sku && selectShape.sku.price)
        customSpu.value.price_detail.sku_price = new BigNumber(selectShape.sku.price).toFixed(2) as any
    }
  }
  quantity.value = 1
})

const colorChangeClick = (value: any) => {
  selectedColor.value = value
  currentCustomSpuData.$patch({ selectedColor: selectedColor.value })
  filterByColorCustomSkus.value = filterSkusByColor(selectedColor.value!, customSpu.value?.skus || [])
  sizes.value = filterByColorCustomSkus.value.map(sku => {
    const skuAttrs = is_custom.value ? sku.sku?.attrs : sku.attrs as any
    return (skuAttrs.find((a: any) => {
      return a.attribute.key === 'size'
    }) || {}).value || ''
  }).filter(s => {
    return s != ''
  })
  sizeSort(sizes.value)
  if (sizes.value.length > 0) {
    const select = selectedSize.value && sizes.value.includes(selectedSize.value) ? selectedSize.value : sizes.value[0]
    sizeChangeClick(select)
  }
  emits('colorChange', { color: selectedColor.value })
}
const sizeSort = (arr: any) => {
  arr.sort((attrA: any, attrB: any) => {
    const sizeValueA = attrA.match(/\d+(\.\d+)?/g)
    const sizeValueB = attrB.match(/\d+(\.\d+)?/g)
    if (!sizeValueA || !sizeValueB)
      return
    if (sizeValueA[0] < sizeValueB[0]) {
      return -1
    }
    if (sizeValueA[0] > sizeValueB[0]) {
      return 1
    }
    return 0
  })
  return arr
}
const filterSkus = (key: string, value: string, arr: CartDataSku[]) => {
  return arr.filter(k => {
    const skuAttrs = is_custom.value ? k.sku?.attrs : k.attrs as any
    return skuAttrs.find((a: any) => {
      return a.attribute.key === key && a.value === value
    })
  })
}

const sizes = ref<string[]>([])
const sizeChangeClick = (value: any) => {
  selectedSize.value = value
  selectedCustomSkus.value = filterSkus('size', selectedSize.value, filterByColorCustomSkus.value)
}
const is_custom = ref<boolean>(false)
const image = ref<CustomizedImage>()
const spuImage = ref<CustomizedImage>()
const alt_image = ref<any>()
const spuPickImage = computed(() => {
  const pickImage = !is_custom.value ? findCustomableImage(selectedColor.value!, spuImage.value!) : ''
  return pickImage
})
// 显示
const setProduct = (value: CartData) => {
  is_custom.value = value.is_custom
  customPrice.value = 0
  const { colors: cs } = convertSpuToSimpleSpu(value)
  colors.value = cs.sort(sortBy('value', false))
  customSpu.value = value
  customSpu.value.price_detail.custom_price?.forEach((r: CustomPrice) => {
    if (r.technologies?.length)
      r.technology = r.technologies[0]
    else
      r.technology = {
        id: undefined,
        name: '热转印'
      } as Technology
  })
  !value.is_custom ? '' : image.value = convertToCustomizedImage(value.customizedImages[0])
  if (colors.value.length > 0) {
    colorChangeClick(colors.value[0])
  }
  spuImage.value = value.image[0]
  const altImage = !value.is_custom ? value.image[0].altImages : image.value?.altImages
  alt_image.value = altColorSelect(altImage)
}
const altColorSelect = (altImage: any) => {
  const altArr = [] as any
  altImage?.forEach((alt: AltImage) => {
    const data = {
      alt_image: alt.alt_image,
      color: alt.attrs ? alt.attrs[0].object_value : {}
    }
    altArr.push(data)
  })
  return altArr
}
// 隐藏
const reset = () => {
  quantity.value = 1
  selectedSize.value = ''
  selectedColor.value = undefined
  customSpu.value = undefined
}

// 加入购物车
const addProduct = async () => {
  if (selectedColor.value !== undefined && selectedSize.value !== '') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const item: any = {
      spu_custom_areas: {},
    }
    if (customSpu.value?.customizedImages) {
      item.spu_customable_image_id = []
      customSpu.value?.customizedImages.forEach((r) => {
        item.spu_customable_image_id.push(r.customableImage.id)
      })
    }

    // 只保存选择 不保存价格
    if (customSpu.value?.price_detail.custom_price) {
      item.technology = []
      customSpu.value?.price_detail.custom_price.forEach((r) => {
        if (r.technology)
          item.technology.push({
            customableImageId: r.id,
            id: r.technology?.id,
            name: r.technology?.name
          })
      })
    }
    item.is_custom = is_custom.value
    item.spu_name = customSpu.value?.spu_name
    item.spu_id = customSpu.value?.spu_id
    item.custom_spu_name = !is_custom.value ? null : customSpu.value?.name
    item.quality = quantity.value
    item.custom_spu_id = !is_custom.value ? null : customSpu.value?.custom_spu_id
    item.image = !is_custom.value ? spuPickImage.value : image.value?.customized_image
    item.spu_customable_image = !is_custom.value ? null : findCustomableImage(selectedColor.value, image.value as CustomizedImage)
    item.spu_custom_width = !is_custom.value ? null : image.value?.width
    item.spu_custom_height = !is_custom.value ? null : image.value?.height
    item.spu_custom_areas = !is_custom.value ? null : image.value?.custom_areas
    item.sku_price = customSpu.value!.price_detail.sku_price || undefined
    item.references = customSpu.value!.references
    const customSku = selectedCustomSkus.value[0] || {}
    if (customSku) {
      item.custom_sku_id = !is_custom.value ? null : customSku.id
      item.sku_id = !is_custom.value ? customSku.id : customSku.sku?.id
      item.sku_code = customSku.code
      const skuAttrs = !is_custom.value ? customSku.attrs : customSku.sku?.attrs
      item.sku_attrs = skuAttrs?.filter(a => {
        // @Todo
        return a.attribute.key === 'color' || a.attribute.key === 'size' || a.attribute.key === 'weight'
      })
    }
    return await shoppingCart.addItem(item).then(() => {
      shoppingCart.fetch()
      message.success(t('CustomSpus.AddToCart.addToCartSuccess'))
    })
  }
  else {
    return Promise.reject()
  }
}

const hiddenContainer = ref()


defineExpose({
  setProduct,
  addProduct,
  reset,
})
</script>
<style scoped lang="less">
.technology {
  display: flex;
  align-items: center;


}

.product {
  font-size: 14px;
  font-family: 'Microsoft YaHei';
  font-weight: 400;
  color: #111111;

  .selling-price {
    margin-top: 18px;
  }

  .price {
    margin-left: 5px;
    color: #FAAD14;
    font-size: 15px;
  }

  .spuInfo {
    border-bottom: 1px solid #f3f3f3;
    margin-bottom: 10px;
    display: flex;
    align-items: center;

    .hidden {
      width: 100px;
      height: 100px;
      position: absolute;
      visibility: hidden;
      display: block;
    }

    .viewer {
      width: 100px;
      height: 100px;
      margin: 0 auto;
      display: flex;
      justify-content: center;
      align-items: center;

      @-moz-document url-prefix() {
        :deep(.fabric-div) {
          width: 100px !important;
          height: 100px !important;
        }

        :deep(.canvas-container) {
          width: 100px !important;
          height: 100px !important;
        }

        :deep(.lower-canvas) {
          width: 100px !important;
          height: 100px !important;
        }

        :deep(.upper-canvas) {
          width: 100px !important;
          height: 100px !important;
        }
      }
    }
  }


}
</style>
